import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format, isValid } from "date-fns";
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
  TrophyIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Navbar from "../../components/navBar";

const API_URL = process.env.REACT_APP_API_URL;

function getList(responseData) {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.data)) return responseData.data;
  return [];
}

function getFixtureStatus(match) {
  const status = String(match.status || match.matchStatus || "").trim();
  const normalized = status.toLowerCase();
  if (normalized.includes("postpon")) return "Postponed";
  if (normalized.includes("cancel")) return "Cancelled";
  if (normalized.includes("reschedul")) return "Rescheduled";
  return "Scheduled";
}

const UpcomingMatches = () => {
  const [matches, setMatches] = useState([]);
  const [sports, setSports] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedSport, setSelectedSport] = useState("all");
  const [selectedLeague, setSelectedLeague] = useState("all");
  const [selectedDate, setSelectedDate] = useState("all");
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );
  const [isCollapsed, setIsCollapsed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem("desac-sidebar-collapsed") === "true"
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [matchesResponse, sportsResponse, teamsResponse, leaguesResponse] = await Promise.all([
        axios.get(`${API_URL}/matches`),
        axios.get(`${API_URL}/sports`),
        axios.get(`${API_URL}/clubs`),
        axios.get(`${API_URL}/leagues`),
      ]);
      const matchesData = getList(matchesResponse.data);
      const sportsData = getList(sportsResponse.data);
      const teamsData = getList(teamsResponse.data);
      const leaguesData = getList(leaguesResponse.data);
      const teamsById = new Map(teamsData.map((team) => [team.clubId, team]));
      const sportsById = new Map(sportsData.map((sport) => [sport.sportId, sport]));
      const leaguesById = new Map(leaguesData.map((league) => [league.leagueId, league]));
      const now = new Date();

      const upcoming = matchesData
        .filter((match) => {
          const kickoff = new Date(match.date);
          return isValid(kickoff) && kickoff > now;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .map((match) => ({
          ...match,
          homeTeam: teamsById.get(match.homeTeamId) || null,
          awayTeam: teamsById.get(match.awayTeamId) || null,
          sport: sportsById.get(match.sportId) || null,
          league: leaguesById.get(match.leagueId) || null,
        }));

      setMatches(upcoming);
      setSports(sportsData);
      setLeagues(leaguesData);
    } catch (fetchError) {
      console.error("Error fetching upcoming matches:", fetchError);
      setError("We couldn't load the fixture schedule. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const updateViewport = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsCollapsed(window.localStorage.getItem("desac-sidebar-collapsed") === "true");
    };
    const updateCollapsedState = (event) => {
      setIsCollapsed(
        typeof event?.detail?.collapsed === "boolean"
          ? event.detail.collapsed
          : window.localStorage.getItem("desac-sidebar-collapsed") === "true"
      );
    };
    window.addEventListener("resize", updateViewport);
    window.addEventListener("storage", updateCollapsedState);
    window.addEventListener("desac-sidebar-toggle", updateCollapsedState);
    return () => {
      window.removeEventListener("resize", updateViewport);
      window.removeEventListener("storage", updateCollapsedState);
      window.removeEventListener("desac-sidebar-toggle", updateCollapsedState);
    };
  }, []);

  const dates = useMemo(() => {
    const uniqueDates = matches
      .map((match) => new Date(match.date))
      .filter(isValid)
      .map((date) => format(date, "yyyy-MM-dd"));
    return [...new Set(uniqueDates)].sort();
  }, [matches]);

  const filteredMatches = useMemo(() => {
    const query = search.trim().toLowerCase();
    return matches.filter((match) => {
      const matchesSport = selectedSport === "all" || String(match.sportId) === selectedSport;
      const matchesLeague = selectedLeague === "all" || String(match.leagueId) === selectedLeague;
      const kickoff = new Date(match.date);
      const matchesDate = selectedDate === "all" || (isValid(kickoff) && format(kickoff, "yyyy-MM-dd") === selectedDate);
      const searchable = [
        match.homeTeam?.name,
        match.awayTeam?.name,
        match.league?.name,
        match.sport?.name,
        match.location,
      ].filter(Boolean).join(" ").toLowerCase();
      return matchesSport && matchesLeague && matchesDate && (!query || searchable.includes(query));
    });
  }, [matches, search, selectedSport, selectedLeague, selectedDate]);

  const groupedMatches = useMemo(() => filteredMatches.reduce((groups, match) => {
    const dateKey = format(new Date(match.date), "yyyy-MM-dd");
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(match);
    return groups;
  }, {}), [filteredMatches]);

  const clearFilters = () => {
    setSearch("");
    setSelectedSport("all");
    setSelectedLeague("all");
    setSelectedDate("all");
  };
  const sidebarWidth = isDesktop && isCollapsed ? "5rem" : "16rem";
  const hasFilters = search || selectedSport !== "all" || selectedLeague !== "all" || selectedDate !== "all";

  return (
    <>
      <Navbar />
      <main
        style={{ "--upcoming-sidebar-width": sidebarWidth }}
        className="min-h-screen overflow-x-hidden bg-surface-950 pb-12 pt-16 text-white md:ml-64 md:pt-0 lg:ml-[var(--upcoming-sidebar-width)]"
      >
        <header className="border-b border-line bg-surface-900">
          <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-8 sm:px-8 sm:py-10 lg:flex-row lg:items-end lg:justify-between lg:px-10">
            <div className="max-w-2xl">
              <p className="eyebrow-accent inline-flex items-center gap-2">
                <CalendarDaysIcon aria-hidden="true" className="h-4 w-4" />
                MATCH CENTRE
              </p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">Upcoming matches</h1>
              <p className="mt-2 text-sm leading-6 text-gray-400 sm:text-base">
                Browse the schedule and find your next fixture.
              </p>
            </div>
            <div aria-live="polite" className="inline-flex min-h-10 w-fit items-center gap-2 rounded-lg border border-line bg-surface-850 px-3 text-sm text-gray-300">
              <CalendarDaysIcon aria-hidden="true" className="h-4 w-4 text-accent-soft" />
              {loading ? "Loading fixtures…" : `${filteredMatches.length} ${filteredMatches.length === 1 ? "fixture" : "fixtures"}`}
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8 sm:py-8 lg:px-10">
          <section aria-label="Filter upcoming matches" className="mb-7 rounded-xl border border-line bg-surface-900 p-4 sm:p-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <label className="relative block sm:col-span-2 xl:col-span-1">
                <span className="sr-only">Search matches</span>
                <MagnifyingGlassIcon aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search teams, leagues, venues"
                  className="field min-h-11 pl-10 pr-10 text-sm"
                />
                {search && (
                  <button type="button" aria-label="Clear search" onClick={() => setSearch("")} className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-surface-700 hover:text-white">
                    <XMarkIcon aria-hidden="true" className="h-4 w-4" />
                  </button>
                )}
              </label>
              <label>
                <span className="sr-only">Filter by sport</span>
                <select value={selectedSport} onChange={(event) => setSelectedSport(event.target.value)} className="field min-h-11 text-sm">
                  <option value="all">All sports</option>
                  {sports.map((sport) => <option key={sport.sportId} value={sport.sportId}>{sport.name}</option>)}
                </select>
              </label>
              <label>
                <span className="sr-only">Filter by competition</span>
                <select value={selectedLeague} onChange={(event) => setSelectedLeague(event.target.value)} className="field min-h-11 text-sm">
                  <option value="all">All competitions</option>
                  {leagues.map((league) => <option key={league.leagueId} value={league.leagueId}>{league.name}</option>)}
                </select>
              </label>
              <label>
                <span className="sr-only">Filter by date</span>
                <select value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} className="field min-h-11 text-sm">
                  <option value="all">All dates</option>
                  {dates.map((date) => <option key={date} value={date}>{format(new Date(`${date}T00:00:00`), "EEE, MMM d, yyyy")}</option>)}
                </select>
              </label>
            </div>
            <div className="mt-3 flex min-h-6 flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
              <p>Fixtures are ordered by scheduled kickoff time.</p>
              {hasFilters && <button type="button" onClick={clearFilters} className="font-medium text-gray-300 underline decoration-line-strong underline-offset-4 transition-colors hover:text-white">Clear filters</button>}
            </div>
          </section>

          {loading ? (
            <div role="status" aria-label="Loading upcoming matches" className="flex min-h-72 items-center justify-center gap-3 rounded-xl border border-line bg-surface-900 text-sm text-gray-300">
              <span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-surface-600 border-t-accent" />
              Loading fixture schedule…
            </div>
          ) : error ? (
            <div role="alert" className="rounded-xl border border-line bg-surface-900 px-5 py-12 text-center">
              <CalendarDaysIcon aria-hidden="true" className="mx-auto h-9 w-9 text-gray-500" />
              <h2 className="mt-4 text-lg font-semibold text-white">Fixtures are temporarily unavailable</h2>
              <p className="mt-2 text-sm text-gray-400">{error}</p>
              <button type="button" onClick={fetchData} className="btn-primary mt-5 min-h-11 gap-2">
                <ArrowPathIcon aria-hidden="true" className="h-4 w-4" /> Try again
              </button>
            </div>
          ) : filteredMatches.length === 0 ? (
            <div className="rounded-xl border border-line bg-surface-900 px-5 py-12 text-center">
              <CalendarDaysIcon aria-hidden="true" className="mx-auto h-9 w-9 text-gray-500" />
              <h2 className="mt-4 text-lg font-semibold text-white">{matches.length ? "No fixtures match these filters" : "No upcoming fixtures"}</h2>
              <p className="mt-2 text-sm text-gray-400">{matches.length ? "Try a different search or filter selection." : "New fixtures will appear here when they are scheduled."}</p>
              {hasFilters && <button type="button" onClick={clearFilters} className="btn-secondary mt-5 min-h-11">Clear filters</button>}
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedMatches).map(([dateKey, dateMatches], dateIndex) => (
                <section key={dateKey} aria-labelledby={`fixtures-${dateKey}`}>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <CalendarDaysIcon aria-hidden="true" className="h-4 w-4 text-accent-soft" />
                    <h2 id={`fixtures-${dateKey}`} className="text-sm font-semibold text-gray-200 sm:text-base">
                      {format(new Date(`${dateKey}T00:00:00`), "EEEE, MMMM d, yyyy")}
                    </h2>
                    {dateIndex === 0 && <span className="rounded-md border border-accent/25 bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-soft">Next fixtures</span>}
                    <span className="text-xs text-gray-500">{dateMatches.length} {dateMatches.length === 1 ? "match" : "matches"}</span>
                  </div>
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    {dateMatches.map((match, matchIndex) => (
                      <FixtureCard key={match.matchesId} match={match} featured={dateIndex === 0 && matchIndex === 0} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  );
};

function FixtureCard({ match, featured }) {
  const kickoff = new Date(match.date);
  const status = getFixtureStatus(match);
  const exceptional = status !== "Scheduled";

  return (
    <article className={`group overflow-hidden rounded-xl border bg-surface-850 transition-all duration-200 hover:-translate-y-0.5 hover:border-line-strong hover:bg-surface-800 ${featured ? "border-accent/30" : "border-line"}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-surface-900 px-4 py-3 sm:px-5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <TrophyIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-accent-soft" />
          <span className="max-w-full truncate text-sm font-semibold text-gray-200">{match.league?.name || "Competition not available"}</span>
          {match.sport?.name && <span className="rounded-md border border-line bg-surface-850 px-2 py-0.5 text-[10px] font-medium text-gray-400">{match.sport.name}</span>}
        </div>
        <span className={`rounded-md border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${exceptional ? "border-amber-500/20 bg-amber-500/10 text-amber-200" : "border-line bg-surface-850 text-gray-400"}`}>
          {status}
        </span>
      </div>

      <div className="p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <time dateTime={kickoff.toISOString()} className={`inline-flex items-center gap-2 text-sm font-semibold ${featured ? "text-accent-soft" : "text-gray-200"}`}>
            <ClockIcon aria-hidden="true" className="h-4 w-4" />
            {format(kickoff, "h:mm a")}
          </time>
          <span className="text-xs text-gray-500">{format(kickoff, "MMM d, yyyy")}</span>
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 rounded-lg border border-line bg-surface-900 p-3 sm:gap-4 sm:p-4">
          <TeamCell team={match.homeTeam} align="left" />
          <span className="text-xs font-bold tracking-wider text-gray-500">VS</span>
          <TeamCell team={match.awayTeam} align="right" />
        </div>

        <div className="mt-3 flex min-w-0 items-center gap-2 text-xs text-gray-500">
          <MapPinIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
          <span className="truncate">{match.location || "Venue to be confirmed"}</span>
        </div>
      </div>
    </article>
  );
}

function TeamCell({ team, align }) {
  const name = team?.name || (align === "left" ? "Home team TBA" : "Away team TBA");
  const initials = team?.name ? team.name.trim().split(/\s+/).slice(0, 2).map((word) => word[0]).join("").toUpperCase() : "?";
  return (
    <div className={`flex min-w-0 items-center gap-2 sm:gap-3 ${align === "right" ? "flex-row-reverse text-right" : ""}`}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-line bg-surface-800 sm:h-12 sm:w-12">
        {team?.logo ? (
          <img src={team.logo} alt={`${name} logo`} className="h-full w-full object-contain p-1.5" onError={(event) => { event.currentTarget.style.display = "none"; }} />
        ) : (
          <span aria-hidden="true" className="text-xs font-bold text-gray-500">{initials}</span>
        )}
      </span>
      <span className="min-w-0 truncate text-xs font-semibold leading-5 text-gray-200 sm:text-sm">{name}</span>
    </div>
  );
}

export default UpcomingMatches;
